import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import { useMemo } from 'react';
import { POR_PAGINA_PADRAO } from '../services/painelService';
import type { FiltrosPainel } from '../types/Painel';

export const SIM_NAO = ['sim', 'nao'] as const;
export const AXIIS = ['falha', 'pendente'] as const;
export const CONVITE = ['nao_enviado', 'enviado', 'falha'] as const;
export const ENTREGA = ['cadastro', 'outro', 'exterior', 'sem'] as const;
export const ORIGEM_INCLUSAO = ['sincronizacao', 'manual'] as const;
export const SITUACOES = ['elegivel', 'excluido'] as const;
export const IDIOMAS = ['PT', 'EN', 'ES'] as const;
const ORDENAR = [
  'nome',
  'matricula',
  'email',
  'respondidaEm',
  'atualizadaEm',
  'ultimoAcessoEm',
  'acessos',
] as const;

const texto = parseAsString.withDefault('');
const parsers = {
  busca: texto,
  matricula: texto,
  nome: texto,
  email: texto,
  temEmail: parseAsArrayOf(parseAsStringLiteral(SIM_NAO)).withDefault([]),
  respondeu: parseAsArrayOf(parseAsStringLiteral(SIM_NAO)).withDefault([]),
  brinde: parseAsArrayOf(parseAsStringLiteral(SIM_NAO)).withDefault([]),
  axiis: parseAsArrayOf(parseAsStringLiteral(AXIIS)).withDefault([]),
  convite: parseAsArrayOf(parseAsStringLiteral(CONVITE)).withDefault([]),
  acessou: parseAsArrayOf(parseAsStringLiteral(SIM_NAO)).withDefault([]),
  incluidoOrigem: parseAsStringLiteral(ORIGEM_INCLUSAO),
  camiseta: parseAsArrayOf(parseAsString).withDefault([]),
  idioma: parseAsArrayOf(parseAsStringLiteral(IDIOMAS)).withDefault([]),
  entrega: parseAsArrayOf(parseAsStringLiteral(ENTREGA)).withDefault([]),
  respondidaDe: texto,
  respondidaAte: texto,
  acessoDe: texto,
  acessoAte: texto,
  situacao: parseAsStringLiteral(SITUACOES).withDefault('elegivel'),
  ordenar: parseAsStringLiteral(ORDENAR),
  direcao: parseAsStringLiteral(['asc', 'desc']).withDefault('asc'),
  pagina: parseAsInteger.withDefault(1),
  porPagina: parseAsInteger.withDefault(POR_PAGINA_PADRAO),
};

/** Parâmetros que o `q` e `origem` já usavam na URL; o resto leva o nome do filtro. */
const urlKeys = { busca: 'q', incluidoOrigem: 'origem' } as const;

/** Os que não contam como "filtro aplicado" (aba, ordem e página). */
const FORA_DOS_FILTROS = ['situacao', 'ordenar', 'direcao', 'pagina', 'porPagina'] as const;

/** Filtros, aba, ordenação e página da lista na URL (`nuqs`). Qualquer mudança, menos de página, volta a `pagina` para 1. */
export function useFiltrosParticipantes() {
  const [bruto, setBruto] = useQueryStates(parsers, { urlKeys });

  const filtros = useMemo<FiltrosPainel>(
    () => ({
      ...bruto,
      incluidoOrigem: bruto.incluidoOrigem ?? '',
      ordenar: bruto.ordenar ?? '',
    }),
    [bruto],
  );

  /** `''` e `[]` saem da URL. */
  const alterar = (parcial: Partial<FiltrosPainel>) => {
    const limpo = Object.fromEntries(
      Object.entries(parcial).map(([k, v]) => [
        k,
        v === '' || (Array.isArray(v) && v.length === 0) ? null : v,
      ]),
    );
    return setBruto({ ...limpo, ...('pagina' in parcial ? {} : { pagina: null }) });
  };

  const limparFiltros = () =>
    setBruto({
      ...Object.fromEntries(
        Object.keys(parsers)
          .filter((k) => !(FORA_DOS_FILTROS as readonly string[]).includes(k))
          .map((k) => [k, null]),
      ),
      pagina: null,
    });

  /** A seleção em lote vale para exatamente estes filtros: mudou algo, ela é descartada (R3). */
  const chaveDaSelecao = JSON.stringify({ ...filtros, pagina: 0 });
  const filtrosAtivos = Object.entries(filtros).some(
    ([k, v]) =>
      !(FORA_DOS_FILTROS as readonly string[]).includes(k) &&
      !(v === '' || (Array.isArray(v) && v.length === 0)),
  );

  return { filtros, alterar, limparFiltros, chaveDaSelecao, filtrosAtivos };
}
